import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TeamProvider, useTeam } from './context/TeamContext';
import { DevModeProvider } from './context/DevModeContext';
import { LicenceProvider } from './context/LicenceContext';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import SelectOrganisation from './pages/SelectOrganisation';
import Dashboard from './pages/areas/Dashboard';
import QuoteRequests from './pages/areas/QuoteRequests';
import Phonebook from './pages/areas/Phonebook';
import Fleet from './pages/areas/Fleet';
// Hidden for the MVP — see the commented-out routes below.
// import Jobs from './pages/areas/Jobs';
// import Resources from './pages/areas/Resources';
// import Inventory from './pages/areas/Inventory';
// import Reports from './pages/areas/Reports';
import Settings from './pages/areas/Settings';
import Support from './pages/areas/Support';
import SupportRequestDetails from './pages/SupportRequestDetails';
import QuoteRequestDetails from './pages/QuoteRequestDetails';
import InsurerGuidelines from './pages/InsurerGuidelines';
import Layout from './components/Layout';
import { useTheme, applyTheme } from './hooks/useTheme';
import BrandSpinner from './components/BrandSpinner';

// Protected routes that require both authentication and team selection
function ProtectedRoutes() {
  const { hasTeam, loading: teamLoading } = useTeam();
  const navigate = useNavigate();

  useEffect(() => {
    // If not loading and no team selected, redirect to team selection
    if (!teamLoading && !hasTeam) {
      console.log('No team selected, redirecting to team selection');
      // Pass the current pathname so we can return here after team selection
      const returnTo = window.location.pathname + window.location.search;
      navigate('/select-organisation', { replace: true, state: { returnTo } });
    }
  }, [hasTeam, teamLoading, navigate]);

  // Show loading while checking team
  if (teamLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BrandSpinner size={64} />
      </div>
    );
  }

  // If no team, don't render routes (redirect will happen in useEffect)
  if (!hasTeam) {
    return null;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/quote-requests" element={<QuoteRequests />} />
        <Route path="/quote-requests/:id" element={<QuoteRequestDetails />} />
        <Route path="/insurer-guidelines/:insurerConfigId" element={<InsurerGuidelines />} />
        {/* Commented out for the MVP, alongside their sidebar items in
            Layout.js. Hiding the link alone is not enough: with the route still
            mounted the page stays reachable by typing the URL, and a bookmark
            from before the cut would open a half-finished screen. With no route
            these paths fall through to the catch-all below and land on the
            dashboard. Uncomment a route and its import to bring one back. */}
        {/* <Route path="/jobs" element={<Jobs />} /> */}
        <Route path="/phonebook" element={<Phonebook />} />
        {/* <Route path="/resources" element={<Resources />} /> */}
        {/* <Route path="/inventory" element={<Inventory />} /> */}
        <Route path="/fleet" element={<Fleet />} />
        {/* <Route path="/reports" element={<Reports />} /> */}
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/:tab" element={<Settings />} />
        <Route path="/support" element={<Support />} />
        <Route path="/support/:id" element={<SupportRequestDetails />} />
        {/* Billing lives under organisation settings. Kept as a redirect so
            existing links — and any Lemon Squeezy return URL still pointing
            here — land in the right place. */}
        <Route path="/billing" element={<Navigate to="/settings/general" replace />} />
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/register" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BrandSpinner size={64} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/select-organisation" element={<SelectOrganisation />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // User is authenticated, now check for team selection.
  // /register stays reachable here on purpose: a brand new account has a
  // session but no organisation yet, and that is exactly the state step two of
  // signup exists to resolve. Routing it into ProtectedRoutes would bounce it
  // to organisation selection, which has nothing to select.
  return (
    <Routes>
      <Route path="/register" element={<Register />} />
      <Route path="/select-organisation" element={<SelectOrganisation />} />
      <Route path="*" element={<ProtectedRoutes />} />
    </Routes>
  );
}

/**
 * Shown when a render-phase crash escapes every component below it. Before this
 * existed the app white-screened and reported nothing — the crash never reached
 * Sentry because there was no boundary to catch it.
 *
 * Deliberately plain: it renders outside the providers, so it cannot assume the
 * theme, the session or the router are in a usable state.
 */
const CrashFallback = () => (
  <div className="min-h-dvh flex flex-col items-center justify-center gap-4 p-6 text-center">
    <p className="text-muted-foreground max-w-md">
      Something went wrong and this screen could not load. The error has been reported.
    </p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
    >
      Reload
    </button>
  </div>
);

function App() {
  // The saved theme (then the browser default) applies to every screen, not
  // just the ones inside Layout — sign in and organisation selection included.
  useTheme();
  useEffect(() => {
    applyTheme();
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<CrashFallback />}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <TeamProvider>
            <LicenceProvider>
              <DevModeProvider>
                <AppContent />
                <Toaster position="top-right" closeButton />
              </DevModeProvider>
            </LicenceProvider>
          </TeamProvider>
        </AuthProvider>
      </Router>
    </Sentry.ErrorBoundary>
  );
}

export default App;
